import { PureComponent } from "react";
import KuikaBadge, { KuikaBadgeColor } from "../../ui-kit/kuika-badge/kuika-badge";

export type ZendeskTicketStatus = "open" | "pending" | "solved";

interface Props {
  status: ZendeskTicketStatus;
  /**
   * Emits the canonical `openStatus` / `pendingStatus` / `solvedStatus` hook. Only the open
   * ticket's own header sets it: the E2E suite queries those ids without `.first()`, so a
   * second badge carrying the same id — one per row in the list, one per card on the board —
   * would turn every status assertion into a strict-mode failure.
   */
  withCanonicalTestId?: boolean;
}

interface StatusPresentation {
  label: string;
  color: KuikaBadgeColor;
  testId: string;
}

/**
 * The Helpdesk's fixed status colours — ONE map, read as a traffic light: **solved is green**
 * (done), pending yellow (waiting on someone), open blue (live, still needs a person). Green used
 * to mean OPEN and solved was grey, which had it backwards twice over — green reads as "finished",
 * and a mostly-solved inbox was a wall of grey with nothing to scan. Red stays out: this product
 * reserves it for errors.
 *
 * These are the STATUS colours only. A ticket's `#id` is coloured by `ticket-id-color.ts` instead —
 * status already has a chip in the same row, and tinting the id from it made every row in a solved
 * inbox the same colour again.
 */
const STATUS_PRESENTATION: Record<ZendeskTicketStatus, StatusPresentation> = {
  open: { label: "Open", color: "blue", testId: "openStatus" },
  pending: { label: "Pending", color: "yellow", testId: "pendingStatus" },
  solved: { label: "Solved", color: "green", testId: "solvedStatus" }
};

/** The badge colour for a status, for the surfaces that tint something OTHER than the status chip. */
export const getStatusBadgeColor = (status: ZendeskTicketStatus): KuikaBadgeColor => STATUS_PRESENTATION[status].color;

class ZendeskStatusBadge extends PureComponent<Props> {
  render() {
    const presentation: StatusPresentation | undefined = STATUS_PRESENTATION[this.props.status];

    if (!presentation) return null;

    return (
      <KuikaBadge
        content={presentation.label}
        color={presentation.color}
        size="small"
        dataTestId={this.props.withCanonicalTestId ? presentation.testId : undefined}
      />
    );
  }
}

export default ZendeskStatusBadge;
